/**
 * This tracks login/signup impressions and clicks
 * using LinkedIn Tracking
 */
import TrackingUtils from './tracking-utils';

(function(win) {

  // Dependencies.
  var ss = win.slideshare_object,
    utils = ss.utils,

    // Constants
    events = {
      SlideShareLoginImpressionEvent: "SlideShareLoginImpressionEvent",
      SlideShareLoginClickEvent: "SlideShareLoginClickEvent"
    };

    var impressionTypes = {
      login: 'LOGIN',
      signup: 'SIGNUP'
    };

    // Refer to the list of possible sources in ajax_signup.js
    var sources = {
      upload: 'UPLOAD',
      download: 'DOWNLOAD',
      clip: 'CLIP',
      favorite: 'LIKE',
      comments: 'COMMENT',
      follow: 'FOLLOW',
      topnav: 'TOPNAV'
    };

    var channels = {
      facebook: 'FACEBOOK',
      scribd: 'SCRIBD',
      linkedin: 'LINKEDIN',
      linkedinUno: 'LINKEDIN_REG',
      slideshare: 'SLIDESHARE'
    };

    var pageViewTypes = {
      full: 'FULL',
      partial: 'PARTIAL'
    };

  /**
   * Creates a new LoginTracker instance.
   *
   * @constructor
   */
  function LoginTracker(trackingId) {
    // Set up tracking utils
    this.trackingUtils = new TrackingUtils();
    this.appId = this.trackingUtils.getAppId('identity');
    this.trackingId = trackingId ? trackingId : this.trackingUtils.generateTrackingID();
    this.eventBodyDefaults = {
      requestHeader: {
        pageKey: this.trackingUtils.getPageKey(),
        path: win.document.location.pathname
      }
    };
  }

  /**
   * Fires a SlideShareLoginImpressionEvent
   *
   * @param {String} impressionType - Login or signup impression
   * @param {String} pageViewType - Full page or partial modal page
   * @param {Array}  channels - List of channels displayed
   * @param {String} source - Source page
   * @param {String} sourceTrackingId - Source page tracking id
   */
  LoginTracker.prototype._trackImpression = function(impressionType, pageViewType, channels, source, sourceTrackingId) {
    var sourceForTracking = sources[source];

    var eventBodyExtras = {
      slideShareGuestUrn: this.trackingUtils.getSlideShareGuest(),
      loginTrackingId: this.trackingId,
      sourceTrackingId: sourceTrackingId,
      source: sourceForTracking,
      channels: channels,
      loginImpressionType: impressionType,
      pageViewType: pageViewType
    };
    var eventBody = $.extend({}, this.eventBodyDefaults, eventBodyExtras);

    this.trackingUtils.recordTrackingEvent(
      this.appId,
      events.SlideShareLoginImpressionEvent,
      eventBody
    );
  };

  /**
   * Fires a SlideShareLoginImpressionEvent for the login page
   *
   * @param {String} source - Source page
   * @param {String} sourceTrackingId - Source page tracking id
   */
  LoginTracker.prototype.trackLoginPageImpression = function(source, sourceTrackingId) {
    this._trackImpression(impressionTypes.login, pageViewTypes.full, [channels.facebook, channels.linkedin, channels.slideshare], source, sourceTrackingId)
  };

  /**
   * Fires a SlideShareLoginImpressionEvent for the signup page
   *
   * @param {String} source - Source page
   * @param {String} sourceTrackingId - Source page tracking id
   */
  LoginTracker.prototype.trackSignupPageImpression = function(source, sourceTrackingId) {
    this._trackImpression(impressionTypes.signup, pageViewTypes.full, [channels.facebook, channels.linkedin, channels.slideshare], source, sourceTrackingId)
  };

  /**
   * Fires a SlideShareLoginImpressionEvent for the uno signup page with only
   * Li UNO signup
   *
   * @param {String} source - Source page
   * @param {String} sourceTrackingId - Source page tracking id
   */
  LoginTracker.prototype.trackUnoSignupPageImpression = function(source, sourceTrackingId) {
    this._trackImpression(impressionTypes.signup, pageViewTypes.full, [channels.linkedinUno], source, sourceTrackingId)
  };

  /**
   * Fires a SlideShareLoginImpressionEvent for the org signup page with only
   * slideshare company signup
   *
   * @param {String} source - Source page
   * @param {String} sourceTrackingId - Source page tracking id
   */
  LoginTracker.prototype.trackOrgSignupPageImpression = function(source, sourceTrackingId) {
    this._trackImpression(impressionTypes.signup, pageViewTypes.full, [channels.slideshare], source, sourceTrackingId)
  };

  /**
   * Fires a SlideShareLoginImpressionEvent for the login modal with LinkedIn login
   *
   * @param {String} source - Source page
   * @param {String} sourceTrackingId - Source page tracking id
   */
  LoginTracker.prototype.trackLinkedinLoginModalImpression = function(source, sourceTrackingId) {
    this._trackImpression(impressionTypes.login, pageViewTypes.partial, [channels.linkedin], source, sourceTrackingId)
  };

  /**
   * Fires a SlideShareLoginImpressionEvent for the login modal with SlideShare login
   *
   * @param {String} source - Source page
   * @param {String} sourceTrackingId - Source page tracking id
   */
  LoginTracker.prototype.trackSlideShareLoginModalImpression = function(source, sourceTrackingId) {
    this._trackImpression(impressionTypes.login, pageViewTypes.partial, [channels.slideshare], source, sourceTrackingId)
  };

  /**
   * Fires a SlideShareLoginImpressionEvent for the signup modal
   *
   * @param {String} source - Source page
   * @param {String} sourceTrackingId source page tracking id
   */
  LoginTracker.prototype.trackSignupModalImpression = function(source, sourceTrackingId) {
    this._trackImpression(impressionTypes.signup, pageViewTypes.partial, [channels.linkedin], source, sourceTrackingId)
  };

  /**
   * Fires a SlideShareLoginClickEvent
   *
   * @param {String} channel - Channel from where the click was triggered
   */
  LoginTracker.prototype._trackClick = function(channel) {
    var eventBodyExtras = {
      slideShareGuestUrn: this.trackingUtils.getSlideShareGuest(),
      loginTrackingId: this.trackingId,
      channel: channel
    };
    var eventBody = $.extend({}, this.eventBodyDefaults, eventBodyExtras);
    const source = getUrlVar("login_source")
    const path = win.document.location.pathname

    this.trackingUtils.recordTrackingEvent(
      this.appId,
      events.SlideShareLoginClickEvent,
      eventBody
    );

    this.logEventToAmplitude("login_attempt", channel, source, path);
  };
  /**
   * Fires a SlideShareLoginClickEvent
   *
   * @param {String} channel - Track Login Errors
   */
   LoginTracker.prototype._trackLoginError = function(errorLog) {
    var eventBodyExtras = {
      slideShareGuestUrn: this.trackingUtils.getSlideShareGuest(),
      loginTrackingId: this.trackingId,
      errorLog: errorLog
    };
    var eventBody = $.extend({}, this.eventBodyDefaults, eventBodyExtras);

    this.trackingUtils.recordTrackingEvent(
      this.appId,
      events.SlideShareLoginClickEvent,
      eventBody
    );

    const source = getUrlVar("login_source")
    const path = win.document.location.pathname

    this.logEventToAmplitude("login_failure", channels.slideshare, source, path)
  };

  LoginTracker.prototype.trackDisabledFacebookLogin = function() {
    const source = getUrlVar('login_source');
    const path = win.document.location.pathname;
    this.logEventToAmplitude(
      'facebook_disabled_login_attempt',
      'facebook',
      source,
      path
    );

  }

  /**
   * Fires a SlideShareLoginClickEvent for SlideShare CTA
   */
  LoginTracker.prototype.trackSlideShareClick = function() {
    this._trackClick(channels.slideshare);
  };

  /**
   * Fires a SlideShareLoginClickEvent for Facebook CTA
   */
  LoginTracker.prototype.trackFacebookClick = function() {
    this._trackClick(channels.facebook);
  };

  /**
   * Fires a SlideShareLoginClickEvent for Scribd CTA
   */
  LoginTracker.prototype.trackScribdClick = function() {
    this._trackClick(channels.scribd);
  };

  /**
   * Fires a SlideShareLoginClickEvent for LinkedIn CTA
   */
  LoginTracker.prototype.trackLinkedinClick = function() {
    this._trackClick(channels.linkedin);
  };

  LoginTracker.prototype.logEventToAmplitude = function(name, channel, source, path) {
    const eventOptions = {
      source: source,
      channel: channel,
      path: path
    }

    amplitude.getInstance().logEvent(name, eventOptions)
  }

  /**
   * Export Login Tracker
   */
  utils.exports('tracking.LoginTracker', LoginTracker);

}(window));
